import type { QuizAnswers, ApiRequest, BusinessQuizAnswers, ConsumerQuizAnswers } from '../types';
import { generatePrompt } from './promptGenerator';

function annualIncomeFromMonthly(monthlyIncome: string): string {
  const incomeMap: Record<string, string> = {
    'under3k': '$0-$36,000',
    '3k-5k': '$36,000-$60,000',
    '5k-8k': '$60,000-$96,000',
    '8k-12k': '$96,000-$144,000',
    '12k-20k': '$144,000-$240,000',
    'over20k': '$240,000+',
  };
  return incomeMap[monthlyIncome] || 'Not specified';
}

function annualIncomeFromRevenue(monthlyRevenue: string): string {
  const revenueMap: Record<string, string> = {
    'under10k': '$0-$120,000',
    '10k-25k': '$120,000-$300,000',
    '25k-50k': '$300,000-$600,000',
    '50k-100k': '$600,000-$1.2M',
    '100k-250k': '$1.2M-$3M',
    'over250k': '$3M+',
  };
  return revenueMap[monthlyRevenue] || 'Not specified';
}

function ageGroupToLabel(ageGroup: string): string {
  const ageMap: Record<string, string> = {
    '18-24': '18-24',
    '25-34': '25-34',
    '35-44': '35-44',
    '45-54': '45-54',
    '55-64': '55-64',
    '65+': '65+',
  };
  return ageMap[ageGroup] || 'Not specified';
}

function industryToOccupation(industry: string): string {
  return industry || 'Business Owner';
}

function goalsToProductGoal(goals: string[]): string[] {
  const goalMap: Record<string, string> = {
    'save_emergency': 'Emergency Savings',
    'invest': 'Investing',
    'pay_debt': 'Debt Payoff',
    'retirement': 'Retirement Planning',
    'budget': 'Guilt-Free Spending',
    'buy_home': 'Home Purchase',
    'family': 'Family Financial Security',
    'travel': 'Travel Savings',
    'cash_flow': 'Cash Flow Management',
    'tax_planning': 'Tax Planning',
    'growth': 'Business Growth',
  };

  return goals.map((goal) => goalMap[goal] || goal);
}

export function quizToApiRequest(answers: Partial<QuizAnswers>): ApiRequest | null {
  if (!answers.userType) return null;

  const isBusinessUser = answers.userType === 'BUSINESS';
  const businessAnswers = answers as BusinessQuizAnswers;
  const consumerAnswers = answers as ConsumerQuizAnswers;

  const profile = {
    USER_TYPE: answers.userType,
    ANNUALINCOME: isBusinessUser
      ? annualIncomeFromRevenue(businessAnswers.monthlyRevenue)
      : annualIncomeFromMonthly(consumerAnswers.monthlyIncome),
    MONTHLY_INCOME_KEY: isBusinessUser
      ? businessAnswers.monthlyRevenue
      : consumerAnswers.monthlyIncome,
    AGE_GROUP: isBusinessUser ? 'Business' : ageGroupToLabel(consumerAnswers.ageGroup),
    OCCUPATION: isBusinessUser
      ? industryToOccupation(businessAnswers.industry)
      : 'Employee',
    PRODUCTGOAL: goalsToProductGoal(answers.goals || []),
  };

  const prompt = generatePrompt(answers);

  return { profile, prompt };
}
